import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Pause, Play } from 'lucide-react';
import { toast } from 'sonner';
import type { CallOutcome } from '@fenix/shared';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { StatusBadge } from '@/components/StatusBadge';
import { DataTablePagination } from '@/components/DataTablePagination';
import { campaignsApi } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';
import {
  CAMPAIGN_STATUS_LABEL,
  CAMPAIGN_STATUS_VARIANT,
  OUTCOME_LABEL,
  fmtDateTime,
  fmtPhone,
  SKIP_REASON_LABEL,
} from '@/lib/format';
import { useCan } from '@/features/auth/store';

const CL_STATUS: Record<string, string> = {
  pending: 'Pendente',
  scheduled: 'Agendado',
  calling: 'Ligando',
  done: 'Concluído',
  skipped: 'Pulado',
};

export function CampaignDetailPage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const canEdit = useCan('campaigns.manage');
  const [page, setPage] = useState(1);
  const q = useQuery({
    queryKey: ['campaigns', id],
    queryFn: () => campaignsApi.get(id),
    refetchInterval: 10_000,
  });
  const leads = useQuery({
    queryKey: ['campaigns', id, 'leads', page],
    queryFn: () => campaignsApi.leads(id, page, 50),
    refetchInterval: 10_000,
  });

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['campaigns'] });
  const start = useMutation({
    mutationFn: () => campaignsApi.start(id),
    onSuccess: () => {
      toast.success('Campanha iniciada');
      invalidate();
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Erro'),
  });
  const pause = useMutation({
    mutationFn: () => campaignsApi.pause(id),
    onSuccess: () => {
      toast.success('Campanha pausada');
      invalidate();
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Erro'),
  });

  const c = q.data;
  if (q.isLoading) return <Skeleton className="h-64 w-full" />;
  if (!c) return <p className="text-muted-foreground">Campanha não encontrada.</p>;
  const pct = c.totalLeads ? (c.completedLeads / c.totalLeads) * 100 : 0;

  return (
    <div>
      <PageHeader
        title={c.name}
        description={`${c.language} · script "${c.scriptTemplate.name}" · criada em ${fmtDateTime(c.createdAt)}`}
        actions={
          <>
            <Button variant="ghost" asChild>
              <Link to="/campanhas">
                <ArrowLeft /> Voltar
              </Link>
            </Button>
            {canEdit && c.status === 'running' && (
              <Button variant="outline" onClick={() => pause.mutate()}>
                <Pause /> Pausar
              </Button>
            )}
            {canEdit && c.status !== 'running' && c.status !== 'done' && (
              <Button onClick={() => start.mutate()}>
                <Play /> {c.status === 'paused' ? 'Retomar' : 'Iniciar'}
              </Button>
            )}
          </>
        }
      />

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="space-y-3 p-5">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Status</span>
              <StatusBadge
                label={CAMPAIGN_STATUS_LABEL[c.status]}
                className={CAMPAIGN_STATUS_VARIANT[c.status]}
              />
            </div>
            <Progress value={pct} />
            <p className="text-sm">
              <span className="text-2xl font-semibold tabular-nums">{c.completedLeads}</span>{' '}
              <span className="text-muted-foreground">de {c.totalLeads} processados</span>
            </p>
            <p className="text-sm">
              <span className="text-2xl font-semibold tabular-nums text-green-700 dark:text-green-300">
                {c.recoveredLeads}
              </span>{' '}
              <span className="text-muted-foreground">vendas recuperadas</span>
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Leads por etapa</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1.5 text-sm">
              {Object.entries(c.leadStatus).map(([k, v]) => (
                <li key={k} className="flex justify-between">
                  <span>{CL_STATUS[k] ?? k}</span>
                  <span className="tabular-nums">{v}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Resultados</CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(c.outcomes).length ? (
              <ul className="space-y-1.5 text-sm">
                {Object.entries(c.outcomes)
                  .sort((a, b) => b[1] - a[1])
                  .map(([k, v]) => (
                    <li key={k} className="flex justify-between">
                      <span>{OUTCOME_LABEL[k as CallOutcome] ?? k}</span>
                      <span className="tabular-nums">{v}</span>
                    </li>
                  ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">Nenhuma chamada concluída.</p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="mt-4">
        <CardHeader>
          <CardTitle className="text-base">Leads da campanha</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {leads.data?.items.length ? (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Lead</TableHead>
                    <TableHead>Telefone</TableHead>
                    <TableHead>Produto</TableHead>
                    <TableHead>Etapa</TableHead>
                    <TableHead className="text-center">Tent.</TableHead>
                    <TableHead>Próxima tentativa</TableHead>
                    <TableHead>Último resultado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leads.data.items.map((cl) => (
                    <TableRow key={cl.id}>
                      <TableCell>
                        <Link className="font-medium hover:underline" to={`/leads/${cl.lead.id}`}>
                          {cl.lead.name || 'Sem nome'}
                        </Link>
                      </TableCell>
                      <TableCell className="tabular-nums">{fmtPhone(cl.lead.phoneE164)}</TableCell>
                      <TableCell className="text-sm">{cl.lead.productCodename ?? '—'}</TableCell>
                      <TableCell>
                        <StatusBadge
                          label={CL_STATUS[cl.status] ?? cl.status}
                          className={cl.status === 'calling' ? 'animate-pulse bg-warning/20' : ''}
                        />
                      </TableCell>
                      <TableCell className="text-center tabular-nums">{cl.attempts}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {fmtDateTime(cl.nextAttemptAt)}
                      </TableCell>
                      <TableCell className="text-sm">
                        {cl.lastCallId ? (
                          <Link className="hover:underline" to={`/chamadas/${cl.lastCallId}`}>
                            {OUTCOME_LABEL[cl.lastOutcome as CallOutcome] ??
                              SKIP_REASON_LABEL[cl.lastOutcome ?? ''] ??
                              cl.lastOutcome ??
                              '—'}
                          </Link>
                        ) : (
                          (SKIP_REASON_LABEL[cl.lastOutcome ?? ''] ?? cl.lastOutcome ?? '—')
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <DataTablePagination
                page={leads.data.page}
                pageSize={leads.data.pageSize}
                total={leads.data.total}
                onPageChange={setPage}
              />
            </>
          ) : (
            <p className="p-6 text-sm text-muted-foreground">Sem leads.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
